// Co-living account configuration: the one settings resource, working hours,
// public holidays, SLA tiers and the role list the SLA fallback needs.
// Every call goes through api/coLiving (the knowledge proxy to singabot).
import { getSettings, putSettings } from '../../api/coLiving/settings';
import {
  getWorkingHours,
  putWorkingHours,
  listHolidays,
  createHoliday,
  updateHoliday,
  deleteHoliday,
} from '../../api/coLiving/calendar';
import { listSlaTiers, putSlaTier } from '../../api/coLiving/sla';
import { listRoles } from '../../api/coLiving/roles';

export const state = {
  settings: null,
  workingHours: [],
  holidays: [],
  slaTiers: [],
  roles: [],
  uiFlags: {
    isFetchingSettings: false,
    isFetchingCalendar: false,
    isFetchingSla: false,
  },
};

export const getters = {
  getSettings: s => s.settings,
  getWorkingHours: s => s.workingHours,
  getHolidays: s => s.holidays,
  getSlaTiers: s => s.slaTiers,
  getRoles: s => s.roles,
  getActiveRoles: s => s.roles.filter(r => r.active),
  getUiFlags: s => s.uiFlags,
};

export const mutations = {
  setSettings(s, settings) {
    s.settings = settings;
  },
  setWorkingHours(s, rows) {
    s.workingHours = rows;
  },
  setHolidays(s, holidays) {
    s.holidays = holidays;
  },
  setSlaTiers(s, tiers) {
    s.slaTiers = tiers;
  },
  setSlaTier(s, tier) {
    s.slaTiers = s.slaTiers.map(t => (t.tier === tier.tier ? tier : t));
  },
  setRoles(s, roles) {
    s.roles = roles;
  },
  setUiFlag(s, { key, value }) {
    s.uiFlags = { ...s.uiFlags, [key]: value };
  },
};

// Runs `task` with a loading flag, clearing it whatever happens.
async function withFlag(commit, key, task) {
  commit('setUiFlag', { key, value: true });
  try {
    return await task();
  } finally {
    commit('setUiFlag', { key, value: false });
  }
}

export const actions = {
  fetchSettings({ commit }) {
    return withFlag(commit, 'isFetchingSettings', async () => {
      const { data } = await getSettings();
      commit('setSettings', data);
      return data;
    });
  },
  async saveSettings({ commit }, fields) {
    const { data } = await putSettings(fields);
    commit('setSettings', data);
    return data;
  },
  fetchCalendar({ commit }) {
    return withFlag(commit, 'isFetchingCalendar', async () => {
      const [hours, holidays] = await Promise.all([
        getWorkingHours(),
        listHolidays(),
      ]);
      commit('setWorkingHours', hours.data);
      commit('setHolidays', holidays.data);
    });
  },
  async saveWorkingHours({ commit }, rows) {
    const { data } = await putWorkingHours(rows);
    commit('setWorkingHours', data);
    return data;
  },
  async fetchHolidays({ commit }, params) {
    const { data } = await listHolidays(params);
    commit('setHolidays', data);
    return data;
  },
  async addHoliday({ dispatch }, fields) {
    const { data } = await createHoliday(fields);
    await dispatch('fetchHolidays');
    return data;
  },
  async editHoliday({ dispatch }, { id, fields }) {
    const { data } = await updateHoliday(id, fields);
    await dispatch('fetchHolidays');
    return data;
  },
  async removeHoliday({ dispatch }, id) {
    await deleteHoliday(id);
    await dispatch('fetchHolidays');
  },
  fetchSla({ commit }) {
    return withFlag(commit, 'isFetchingSla', async () => {
      const [tiers, roles] = await Promise.all([
        listSlaTiers(),
        listRoles({ status: 'active' }),
      ]);
      commit('setSlaTiers', tiers.data);
      commit('setRoles', roles.data);
    });
  },
  async saveSlaTier({ commit }, { tier, fields }) {
    const { data } = await putSlaTier(tier, fields);
    commit('setSlaTier', data);
    return data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
